'use client';

import React from 'react';
import { Card as AntCard, ConfigProvider } from 'antd';
import type { CardProps as AntCardProps } from 'antd';
import { orionTheme } from '../theme/orion-theme';
import clsx from 'clsx';

export interface CardProps extends AntCardProps {
  /**
   * Additional CSS classes (supports Tailwind overrides)
   */
  className?: string;
}

/**
 * Card component - Ant Design Card with OpenStore Orion theming
 * 
 * This component provides FULL access to all Ant Design Card props while
 * automatically applying the OpenStore Orion theme.
 * 
 * Features:
 * - Orion design specifications
 * - Automatic theming
 * - Tailwind class override support
 * - ALL Ant Design Card features and props
 * 
 * @example
 * ```tsx
 * // All Ant Design Card props are available
 * <Card title="Product Settings">
 *   <p>Configure your product details</p>
 * </Card>
 * 
 * // With all Ant Design features
 * <Card 
 *   title="Bundle Management"
 *   extra={<Button type="primary">Add Bundle</Button>}
 *   actions={[
 *     <Button key="edit">Edit</Button>,
 *     <Button key="delete" danger>Delete</Button>
 *   ]}
 *   cover={<img src="cover.jpg" />}
 *   hoverable
 *   bordered={false}
 *   size="small"
 *   loading={false}
 * >
 *   <Card.Meta 
 *     title="Card Title"
 *     description="Card description"
 *     avatar={<Avatar src="avatar.jpg" />}
 *   />
 * </Card>
 * 
 * // With Tailwind overrides
 * <Card 
 *   title="Custom Card"
 *   className="shadow-lg border-orion-200 hover:shadow-xl transition-shadow"
 * >
 *   <p>Custom styled card</p>
 * </Card>
 * 
 * // Grid cards
 * <Card 
 *   hoverable
 *   style={{ width: 240 }}
 *   cover={<img alt="example" src="https://example.com/image.jpg" />}
 *   actions={[
 *     <Button key="view">View</Button>,
 *     <Button key="edit">Edit</Button>,
 *   ]}
 * >
 *   <Card.Meta title="Product Name" description="Product description" />
 * </Card>
 * ```
 */
export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      children,
      ...props // All other Ant Design props pass through
    },
    ref
  ) => {
    return (
      <ConfigProvider theme={orionTheme}>
        <AntCard
          ref={ref}
          className={clsx(
            'orion-card',
            // Add custom classes for Tailwind overrides
            className
          )}
          {...props} // Pass through ALL Ant Design props
        >
          {children}
        </AntCard>
      </ConfigProvider>
    );
  }
);

Card.displayName = 'Card';

// Export Meta component for card metadata
export const { Meta: CardMeta } = AntCard;